import type { StructuredDocumentData } from "@/shared/types/documentuploadtype";
import { getNationalIdOcrKeys } from "@/shared/utils/nationalId";
import type { CarAdditionalInfoValues } from "../redux/type";

const additionalInfoOcrFieldMap = {
  chassisNumber: ["Chassis Number", "Chassis_Number", "Chassis No", "Chassis_No", "VIN"],
  emiratesId: getNationalIdOcrKeys(),
  vehicleColor: ["Color", "Vehicle Color", "Vehicle_Color"],
  tcfNumber: [
    "TCF No",
    "TCF No.",
    "TFC Number",
    "TCF_Number",
    "Traffic Code Number",
    "Traffic_Code_Number",
  ],
  registrationType: ["Registration Type", "Registration_Type", "Vehicle Type", "Vehicle_Type"],
  plateCode: ["Plate Code", "Plate_Code"],
  plateNumber: ["Plate Number", "Plate_Number", "Plate No", "Plate_No"],
  engineNumber: ["Engine Number", "Engine_Number", "Engine No", "Engine_No"],
} satisfies Partial<Record<keyof CarAdditionalInfoValues, string[]>>;

function getStructuredValue(structuredData: StructuredDocumentData, keys: string[]): string {
  const value = keys
    .map((key) => structuredData[key])
    .find((item) => typeof item === "string" && item.trim());

  return typeof value === "string" ? value.trim() : "";
}

export function getCarAdditionalInfoOcrDefaults(
  structuredData?: StructuredDocumentData
): Partial<CarAdditionalInfoValues> {
  if (!structuredData) {
    return {};
  }

  return Object.entries(additionalInfoOcrFieldMap).reduce<Partial<CarAdditionalInfoValues>>(
    (defaults, [fieldName, ocrKeys]) => {
      const value = getStructuredValue(structuredData, ocrKeys);

      if (value) {
        return {
          ...defaults,
          [fieldName]: value,
        };
      }

      return defaults;
    },
    {}
  );
}

export function mergeCarAdditionalInfoOcrDefaults(
  values: CarAdditionalInfoValues,
  ocrDefaults: Partial<CarAdditionalInfoValues>
): CarAdditionalInfoValues {
  return Object.keys(ocrDefaults).reduce<CarAdditionalInfoValues>((mergedValues, fieldName) => {
    const fieldKey = fieldName as keyof CarAdditionalInfoValues;
    const currentValue = mergedValues[fieldKey];
    const ocrValue = ocrDefaults[fieldKey];

    if (typeof currentValue === "string" && !currentValue && ocrValue) {
      return {
        ...mergedValues,
        [fieldKey]: ocrValue,
      };
    }

    return mergedValues;
  }, values);
}
